import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { FiSend, FiX, FiRefreshCw } from 'react-icons/fi';

const statusBadge = {
  queued: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  sending: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  sent: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  failed: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
};

const CampaignHistory = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [recipients, setRecipients] = useState([]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get('/api/admin/emails/campaigns');
      setCampaigns(r.data.campaigns);
    } catch (error) {
      toast.error('Failed to load campaigns');
    } finally {
      setLoading(false);
    }
  };

  const loadDetail = async (id) => {
    try {
      const r = await axios.get(`/api/admin/emails/campaigns/${id}`);
      setDetail(r.data.campaign);
      setRecipients(r.data.recipients);
    } catch (error) {
      toast.error('Failed to load campaign detail');
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Campaign History</h3>
        <button
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-1.5 px-2 py-1 text-xs font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors disabled:opacity-50"
        >
          <FiRefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {loading ? (
        <div className="p-8 text-center text-xs text-gray-500 dark:text-gray-400">Loading…</div>
      ) : campaigns.length === 0 ? (
        <div className="p-10 text-center">
          <FiSend className="w-8 h-8 mx-auto mb-2 text-gray-300 dark:text-gray-600" />
          <p className="text-sm text-gray-500 dark:text-gray-400">No campaigns sent yet.</p>
        </div>
      ) : (
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-700/50">
            <tr>
              <th className="px-4 py-2 text-left text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">When</th>
              <th className="px-4 py-2 text-left text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Subject</th>
              <th className="px-4 py-2 text-left text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Status</th>
              <th className="px-4 py-2 text-left text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Recipients</th>
              <th className="px-4 py-2 text-left text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Sent</th>
              <th className="px-4 py-2 text-left text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Failed</th>
              <th className="px-4 py-2 text-left text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Sent by</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700/60">
            {campaigns.map(c => (
              <tr
                key={c.id}
                onClick={() => loadDetail(c.id)}
                className="hover:bg-gray-50 dark:hover:bg-gray-700/30 cursor-pointer transition-colors"
              >
                <td className="px-4 py-2 text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">
                  {format(new Date(c.sent_at || c.sentAt || c.created_at || c.createdAt), 'MMM d, HH:mm')}
                </td>
                <td className="px-4 py-2 text-xs text-gray-900 dark:text-white max-w-md truncate">{c.subject}</td>
                <td className="px-4 py-2">
                  <span className={`inline-block px-1.5 py-0.5 text-[10px] font-medium rounded ${statusBadge[c.status] || statusBadge.queued}`}>
                    {c.status}
                  </span>
                </td>
                <td className="px-4 py-2 text-xs text-gray-600 dark:text-gray-400">{c.recipient_count ?? c.recipientCount}</td>
                <td className="px-4 py-2 text-xs text-emerald-700 dark:text-emerald-400 font-medium">{c.sent_count ?? c.sentCount}</td>
                <td className="px-4 py-2 text-xs text-red-600 dark:text-red-400 font-medium">{c.failed_count ?? c.failedCount}</td>
                <td className="px-4 py-2 text-xs text-gray-600 dark:text-gray-400">
                  {c.creator ? `${c.creator.firstName || ''} ${c.creator.lastName || ''}`.trim() : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {detail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/60 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl ring-1 ring-gray-200 dark:ring-gray-700 w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 dark:border-gray-700">
              <div>
                <h3 className="text-base font-semibold text-gray-900 dark:text-white">{detail.subject}</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Sent {format(new Date(detail.sent_at || detail.sentAt || detail.created_at || detail.createdAt), 'MMM d, yyyy HH:mm')}
                  {' · '}
                  <span className={statusBadge[detail.status]?.replace('bg-', 'text-').replace('-100', '-700').replace('-700', '-700') || ''}>
                    {detail.status}
                  </span>
                  {' · '}
                  {detail.sent_count ?? detail.sentCount} sent / {detail.failed_count ?? detail.failedCount} failed
                </p>
              </div>
              <button
                onClick={() => setDetail(null)}
                className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                aria-label="Close"
              >
                <FiX className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto">
              <div className="p-5 border-b border-gray-200 dark:border-gray-700">
                <h4 className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-2">Body</h4>
                <div className="bg-gray-50 dark:bg-gray-900 p-3 rounded-md border border-gray-200 dark:border-gray-700 text-xs max-h-48 overflow-y-auto">
                  <pre className="whitespace-pre-wrap text-gray-700 dark:text-gray-300">{detail.body_html || detail.bodyHtml}</pre>
                </div>
              </div>

              <div className="p-5">
                <h4 className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-2">
                  Recipients ({recipients.length})
                </h4>
                <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                  <thead className="bg-gray-50 dark:bg-gray-700/50">
                    <tr>
                      <th className="px-3 py-1.5 text-left text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Email</th>
                      <th className="px-3 py-1.5 text-left text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Status</th>
                      <th className="px-3 py-1.5 text-left text-[10px] font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">Error</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700/60">
                    {recipients.map(r => (
                      <tr key={r.email}>
                        <td className="px-3 py-1 text-xs text-gray-900 dark:text-white">{r.email}</td>
                        <td className="px-3 py-1">
                          <span className={`inline-block px-1.5 py-0.5 text-[10px] font-medium rounded ${statusBadge[r.status] || statusBadge.queued}`}>
                            {r.status}
                          </span>
                        </td>
                        <td className="px-3 py-1 text-[11px] text-red-600 dark:text-red-400 max-w-xs truncate">
                          {r.error_message || r.errorMessage || ''}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CampaignHistory;
